import * as React from "react";
import { useState } from "react";
import { Field, withTypes } from "react-final-form";
import axios from "axios";
import {
  Button,
  CardActions,
  CircularProgress,
  TextField,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Notification, useTranslate, useNotify } from "react-admin";
import { ENDPOINTS } from "../apiConstants";
import { getConfigGet, getConfigPatch } from "../getConfig";
import { ShowData } from "./transferRewardAmount";

const useStyles = makeStyles((theme) => ({
  main: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
  },
  form: {
    padding: "0 1em 1em 1em",
  },
  input: {
    marginTop: "1em",
  },
  actions: {
    padding: "0 1em 1em 1em",
  },
  center: {
    textAlign: 'center'
  },
  button: {
    margin: '1em'
  },
  tableCell: {
    padding: '4px 8px',
    border: '1px solid gray',
    whiteSpace: 'pre-line' as const,
    fontSize: '0.8rem',
  },
}));

const renderInput = ({
  meta: { touched, error } = { touched: false, error: undefined },
  input: { ...inputProps },
  ...props
}: any) => (
  <TextField
    error={!!(touched && error)}
    helperText={touched && error}
    {...inputProps}
    {...props}
    fullWidth
  />
);

interface FormValues {
  email?: string;
}

const { Form } = withTypes<FormValues>();

const ManageLinkedAccount = () => {
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState<any[]>([]);
  const [connected, setConnected] = useState(false);
  const [canReConnect, setCanReConnect] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const translate = useTranslate();
  const classes = useStyles();
  const notify = useNotify();

  const handleSubmit = (inputData: any) => {
    const { email } = inputData;
    setLoading(true);
    setTableData([]);
    setUserEmail(email);

    let config = getConfigGet({ email });
    axios.get(ENDPOINTS.MANAGE_LINKED_ACCOUNT, config)
      .then((res: any) => {
        setTableData(res.data.tableData || []);
        setConnected(res.data.isLatestConnected || false);
        setCanReConnect(res.data.canReConnect || false);
      })
      .catch((error) => {
        notify(error?.response?.data?.message || 'Failed to fetch linked account info', "error", "", undefined, 50000);
      })
      .finally(() => setLoading(false));
  };

  const handleAction = (action: 'disconnect' | 'reconnect') => {
    if (!userEmail) return;
    setActionLoading(true);

    let headers = getConfigPatch();
    let data = { email: userEmail, action };

    axios.patch(ENDPOINTS.MANAGE_LINKED_ACCOUNT, data, { headers })
      .then((res: any) => {
        notify(res?.data?.message || 'Success', "info", "", undefined, 50000);
        handleSubmit({ email: userEmail });
      })
      .catch((error) => {
        notify(error?.response?.data?.message || 'Action failed', "error", "", undefined, 50000);
      })
      .finally(() => setActionLoading(false));
  };

  const validate = (values: FormValues) => {
    const errors: FormValues = {};
    if (!values.email) {
      errors.email = translate("ra.validation.required");
    }
    return errors;
  };

  return (
    <Form
      onSubmit={handleSubmit}
      validate={validate}
      render={({ handleSubmit }) => (
        <div>
          <form onSubmit={handleSubmit} noValidate>
            <div className={classes.main}>
              <div className={classes.form}>
                <div className={classes.input}>
                  <Field
                    autoFocus
                    name="email"
                    // @ts-ignore
                    component={renderInput}
                    label={translate("pos.email")}
                    disabled={loading}
                  />
                </div>
              </div>
              <CardActions className={classes.actions}>
                <Button
                  variant="contained"
                  type="submit"
                  color="primary"
                  disabled={loading}
                  fullWidth
                >
                  {loading && <CircularProgress size={25} thickness={2} />}
                  Fetch Linked Account Details
                </Button>
              </CardActions>
              <Notification />
            </div>
          </form>

          {tableData.length > 0 && (
            <ShowData record={tableData} tableCenterAlign={false} />
          )}
          {tableData.length > 1 && (
            <div style={{
              maxWidth: '700px',
              margin: '15px auto 0 auto',
              padding: '12px',
              backgroundColor: '#fff3cd',
              border: '1px solid #ffeaa7',
              borderRadius: '4px',
              color: '#856404'
            }}>
              <strong>Note:</strong> If you re-connect the account, make sure you check the withdraw payee as well — from User Details and PPS Payee screens.
            </div>
          )}
          {tableData.length > 1 && (
            <div style={{ textAlign: 'center', margin: '0 0 16px 0', display: 'flex', justifyContent: 'center', gap: 16 }}>
              <Button
                color="secondary"
                variant="contained"
                onClick={() => handleAction('disconnect')}
                disabled={actionLoading || !connected}
              >
                {actionLoading && connected && <CircularProgress size={20} thickness={2} style={{ marginRight: 8 }} />}
                Disconnect Account
              </Button>
              <Button
                color="primary"
                variant="contained"
                onClick={() => handleAction('reconnect')}
                disabled={actionLoading || connected || !canReConnect}
              >
                {actionLoading && !connected && <CircularProgress size={20} thickness={2} style={{ marginRight: 8 }} />}
                Re-Connect Account
              </Button>
            </div>
          )}
        </div>
      )}
    />
  );
};

export default ManageLinkedAccount;
